import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RotateCcw, Menu, X, Popcorn, Film, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import { useSearchLimits } from '../../hooks/useSearchLimits';
import { SearchesLeftBadge } from '../ui/SearchesLeftBadge';
import { OutOfSearchesWall } from '../ui/OutOfSearchesWall';
import { PathSelection } from './steps/PathSelection';
import { GenreSelection } from './steps/GenreSelection';
import { SubgenreSelection } from './steps/SubgenreSelection';
import { FavoriteMovies } from './steps/FavoriteMovies';
import { MoodSelection } from './steps/MoodSelection';
import { CriticallyAcclaimedSelection } from './steps/CriticallyAcclaimedSelection';
import { TimeframeSelection } from './steps/TimeframeSelection';
import { MovieRecommendations } from './steps/MovieRecommendations';
import { WatchlistDropdown } from '../ui/WatchlistDropdown';
import { WatchedMoviesDropdown } from '../ui/WatchedMoviesDropdown';
import { Logo } from '../ui/Logo';
import { getAiConsent, setAiConsent } from '../../lib/aiConsent';
import {
  AIRecommendationConsentSheet,
  AI_CONSENT_SAVE_ERROR,
} from './AIRecommendationConsentSheet';

type ConversationStep = 
  | 'path'
  | 'genres'
  | 'subgenres'
  | 'favorites'
  | 'mood'
  | 'mood-favorites'
  | 'acclaimed'
  | 'timeframe'
  | 'recommendations';

interface UserSelections {
  path: 'genre' | 'mood' | '';
  genres: string[];
  subgenres: string[];
  favorites: string[];
  moods: string[];
  acclaimed: string;
  timeframes: string[];
}

type ConsentPhase = 'checking' | 'required' | 'granted';

const genreBasedSteps: ConversationStep[] = [
  'path',
  'genres',
  'subgenres',
  'favorites',
  'acclaimed',
  'timeframe',
  'recommendations'
];

const moodBasedSteps: ConversationStep[] = [
  'path',
  'mood',
  'mood-favorites',
  'acclaimed',
  'timeframe',
  'recommendations'
];

export function ConversationFlow() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { usage, loading: loadingUsage } = useSearchLimits();
  const outOfSearches = !loadingUsage && usage !== null && usage.remainingSearches <= 0;
  const [consentPhase, setConsentPhase] = useState<ConsentPhase>('checking');
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<ConversationStep>('path');
  const [selections, setSelections] = useState<UserSelections>({
    path: '',
    genres: [],
    subgenres: [],
    favorites: [],
    moods: [],
    acclaimed: '',
    timeframes: []
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const consent = await getAiConsent();
        if (!cancelled) {
          setConsentPhase(consent.granted ? 'granted' : 'required');
        }
      } catch {
        if (!cancelled) {
          setConsentError("Couldn't load your permission. Check your connection and try again.");
          setConsentPhase('required');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const stepOrder = selections.path === 'genre' ? genreBasedSteps : moodBasedSteps;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handlePathSelection = (path: 'genre' | 'mood') => {
    setSelections(prev => ({ ...prev, path }));
    setCurrentStep(path === 'genre' ? 'genres' : 'mood');
  };

  const handleGenreSelection = (genres: string[]) => {
    setSelections(prev => ({ ...prev, genres }));
    setCurrentStep('subgenres');
  };

  const handleSubgenreSelection = (subgenres: string[]) => {
    setSelections(prev => ({ ...prev, subgenres }));
    setCurrentStep('favorites');
  };

  const handleFavoriteMovies = (favorites: string[]) => {
    setSelections(prev => ({ ...prev, favorites }));
    setCurrentStep('acclaimed');
  };

  const handleMoodSelection = (moods: string[]) => {
    setSelections(prev => ({ ...prev, moods }));
    setCurrentStep('mood-favorites');
  };

  const handleMoodFavoriteMovies = (favorites: string[]) => {
    setSelections(prev => ({ ...prev, favorites }));
    setCurrentStep('acclaimed');
  };

  const handleAcclaimedSelection = (acclaimed: string) => {
    setSelections(prev => ({ ...prev, acclaimed }));
    setCurrentStep('timeframe');
  };

  const handleTimeframeSelection = (timeframes: string[]) => {
    setSelections(prev => ({ ...prev, timeframes }));
    setCurrentStep('recommendations');
  };

  const handleBack = () => {
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const handleStartOver = () => {
    setSelections({
      path: '',
      genres: [],
      subgenres: [],
      favorites: [],
      moods: [],
      acclaimed: '',
      timeframes: []
    });
    setCurrentStep('path');
  };

  const handleAllowConsent = async () => {
    setConsentSaving(true);
    setConsentError(null);
    try {
      await setAiConsent(true);
      setConsentPhase('granted');
    } catch {
      setConsentError(AI_CONSENT_SAVE_ERROR);
    } finally {
      setConsentSaving(false);
    }
  };

  const handleConsentRequired = useCallback(() => {
    setConsentError(null);
    setConsentPhase('required');
  }, []);

  const renderCurrentStep = () => {
    const showBackButton = stepOrder.indexOf(currentStep) > 0;

    switch (currentStep) {
      case 'path':
        return (
          <PathSelection 
            onSelect={handlePathSelection}
          />
        );
      case 'genres':
        return (
          <GenreSelection 
            selectedGenres={selections.genres}
            onSelect={handleGenreSelection} 
          />
        );
      case 'subgenres':
        return (
          <SubgenreSelection
            selectedGenres={selections.genres}
            selectedSubgenres={selections.subgenres}
            onSelect={handleSubgenreSelection}
            onBack={handleBack}
            showBackButton={showBackButton}
          />
        );
      case 'favorites':
        return (
          <FavoriteMovies
            genres={selections.genres}
            onSelect={handleFavoriteMovies}
            onBack={handleBack}
            showBackButton={showBackButton}
          />
        );
      case 'mood':
        return (
          <MoodSelection
            selectedMoods={selections.moods}
            onSelect={handleMoodSelection}
            onBack={handleBack}
            showBackButton={showBackButton}
          />
        );
      case 'mood-favorites':
        return (
          <FavoriteMovies
            genres={[]} // Empty array since we're using moods
            onSelect={handleMoodFavoriteMovies}
            onBack={handleBack}
            showBackButton={showBackButton}
          />
        );
      case 'acclaimed':
        return (
          <CriticallyAcclaimedSelection
            selectedOption={selections.acclaimed}
            onSelect={handleAcclaimedSelection}
            onBack={handleBack}
            showBackButton={showBackButton}
          />
        );
      case 'timeframe':
        return (
          <TimeframeSelection
            selectedTimeframes={selections.timeframes}
            onSelect={handleTimeframeSelection}
            onBack={handleBack}
            showBackButton={showBackButton}
          />
        );
      case 'recommendations':
        return (
          <MovieRecommendations
            selections={selections}
            onReset={handleStartOver}
            onBack={handleBack}
            showBackButton={showBackButton}
            onConsentRequired={handleConsentRequired}
          />
        );
      default:
        return null;
    }
  };

  const currentStepIndex = stepOrder.indexOf(currentStep);
  const totalSteps = stepOrder.length;
  const progressPercent = ((currentStepIndex + 1) / totalSteps) * 100;

  return (
    <div className="grain min-h-screen">
      <header className="app-header px-4 pb-4">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="btn-quiet p-2"
                aria-label="Back to home"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <Logo />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden items-center gap-2 md:flex">
              <SearchesLeftBadge />
              <button onClick={handleStartOver} className="btn-quiet px-3 py-2 text-sm">
                <RotateCcw className="h-4 w-4" />
                Start Over
              </button>
              <WatchlistDropdown />
              <WatchedMoviesDropdown />
              <button
                type="button"
                onClick={() => navigate('/account')}
                className="btn-quiet px-3 py-2 text-sm"
              >
                Account
              </button>
              <button onClick={handleSignOut} className="btn-quiet px-3 py-2 text-sm">
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="btn-quiet p-2 md:hidden"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="animate-fade-in mt-4 space-y-1 border-t border-white/[0.06] pt-4 md:hidden">
              <div className="px-4 py-2">
                <SearchesLeftBadge />
              </div>
              <button
                onClick={() => {
                  handleStartOver();
                  setIsMobileMenuOpen(false);
                }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
              >
                <RotateCcw className="h-4 w-4" />
                Start Over
              </button>
              <div
                onClick={() => {
                  navigate('/watchlist');
                  setIsMobileMenuOpen(false);
                }}
                className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
              >
                <Popcorn className="h-4 w-4" />
                Watchlist
              </div>
              <div
                onClick={() => {
                  navigate('/watched');
                  setIsMobileMenuOpen(false);
                }}
                className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
              >
                <Film className="h-4 w-4" />
                Watched Movies
              </div>
              <button
                type="button"
                onClick={() => {
                  navigate('/account');
                  setIsMobileMenuOpen(false);
                }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
              >
                Account
              </button>
              <button
                onClick={() => {
                  handleSignOut();
                  setIsMobileMenuOpen(false);
                }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 md:py-14">
        {/* Step progress */}
        {consentPhase === 'granted' && !loadingUsage && !outOfSearches && (
          <div className="mb-6 px-1">
            <div className="mb-2 flex items-center justify-between text-xs font-medium tracking-wide text-muted-foreground">
              <span className="eyebrow !text-[11px]">
                {currentStep === 'recommendations' ? 'Your picks' : 'Finding your movie'}
              </span>
              <span>
                Step {currentStepIndex + 1} of {totalSteps}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${progressPercent}%`,
                  background: 'linear-gradient(90deg, hsl(358 88% 58%), hsl(39 94% 58%))'
                }}
              />
            </div>
          </div>
        )}

        <div className="panel p-6 sm:p-10">
          {consentPhase === 'checking' ? (
            <div className="flex flex-col items-center justify-center space-y-4 p-8 py-16">
              <div className="h-1 w-40 overflow-hidden rounded-full bg-secondary">
                <div className="animate-progress h-full rounded-full bg-primary" />
              </div>
              <p className="text-sm text-muted-foreground">Checking AI recommendation permission…</p>
            </div>
          ) : consentPhase === 'required' ? (
            <AIRecommendationConsentSheet
              saving={consentSaving}
              error={consentError}
              onAllow={() => {
                void handleAllowConsent();
              }}
              onNotNow={() => {
                if (!consentSaving) navigate('/');
              }}
            />
          ) : loadingUsage ? (
            <div className="flex flex-col items-center justify-center space-y-4 p-8">
              <div className="h-1 w-40 overflow-hidden rounded-full bg-secondary">
                <div className="animate-progress h-full rounded-full bg-primary" />
              </div>
              <p className="text-muted-foreground">Loading...</p>
            </div>
          ) : outOfSearches ? (
            <OutOfSearchesWall />
          ) : (
            <div key={currentStep} className="animate-rise">
              {renderCurrentStep()}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
